import type { FormEventHandler, ReactNode } from 'react';
import type { DefaultValues, FieldValues, Resolver } from 'react-hook-form';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ConfirmOptions } from '@/components/feedback/confirmContext';

/**
 * Overlay design system — shared types and sizes.
 *
 * Pick the overlay by content, not by habit:
 *   ConfirmDialog        a yes/no question (delete, discard, submit).
 *   FormDialog           a small create/edit form, about 6 fields or fewer.
 *   FormDrawer           a larger create/edit form, usually in OverlaySections.
 *   DetailDrawer         read-only details of one record.
 *   ConfigurationDrawer  settings screens with several sections.
 *   AppDialog/AppDrawer  anything else, built on the same shell.
 */

/** Size scale shared by dialogs and drawers. `xxl` takes the full width of the screen. */
export type OverlaySize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

/** Dialog max width (px, or a CSS width) per size. Dialogs go full screen below `sm`. */
export const DIALOG_WIDTHS: Record<OverlaySize, number | string> = {
  xs: 400,
  sm: 600,
  md: 800,
  lg: 1000,
  xl: 1200,
  xxl: '100%',
};

/** Drawer width (px, or a CSS width) per size. Drawers take the full width below `sm`. */
export const DRAWER_WIDTHS: Record<OverlaySize, number | string> = {
  xs: 360,
  sm: 480,
  md: 640,
  lg: 800,
  xl: 1000,
  xxl: '100%',
};

export type OverlayVariant = 'dialog' | 'drawer';

/** Colour of a primary action. `error` marks destructive actions. */
export type OverlayTone = 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'info';

/** Why an overlay asked to close. */
export type OverlayCloseReason = 'backdropClick' | 'escapeKeyDown' | 'closeButton' | 'cancel';

/** Props shared by every overlay shell (AppOverlay, AppDialog, AppDrawer). */
export interface OverlayShellProps {
  open: boolean;
  /** Called once the overlay may close (after busy / dismissible / confirmClose checks). */
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Icon shown before the title. */
  icon?: ReactNode;
  /** Extra controls in the header, before the close button. */
  headerActions?: ReactNode;
  size?: OverlaySize;
  /**
   * Work in progress: shows a progress bar and blocks every way of closing,
   * so a pending save cannot be abandoned half way.
   */
  busy?: boolean;
  /** Close on backdrop click and Escape. Defaults to true. */
  dismissible?: boolean;
  /**
   * Ask before closing — e.g. `isDirty` on a form. `true` uses the standard
   * "Discard changes?" question; pass options to word it differently.
   */
  confirmClose?: boolean | ConfirmOptions;
  /** Rendered below the scrolling body, usually an OverlayFooter. */
  footer?: ReactNode;
  /** When set, the overlay content is a <form> and this is its submit handler. */
  onSubmit?: FormEventHandler<HTMLFormElement>;
  /** Removes the body padding (full-bleed tables, tabs, maps...). */
  disablePadding?: boolean;
  /**
   * Leaves the body unscrolled, for an overlay that scrolls its own panes
   * (ConfigurationDrawer's section layout). Everything else scrolls the body
   * with the app's custom scrollbar.
   */
  disableBodyScroll?: boolean;
  bodySx?: SxProps<Theme>;
  /** Called after the close transition has finished. */
  onExited?: () => void;
  children?: ReactNode;
}

/** One button in an OverlayFooter. */
export interface OverlayAction {
  label: ReactNode;
  onClick?: () => void | Promise<void>;
  /** `submit` submits the overlay's form. Defaults to `button`. */
  type?: 'button' | 'submit';
  tone?: OverlayTone;
  variant?: 'text' | 'outlined' | 'contained';
  startIcon?: ReactNode;
  disabled?: boolean;
  /** Shows a spinner and disables the button. */
  loading?: boolean;
  autoFocus?: boolean;
  /** Accessible name when `label` is not plain text. */
  'aria-label'?: string;
}

/** Props shared by FormDialog and FormDrawer. */
export interface OverlayFormProps<TValues extends FieldValues> {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Zod (or other) resolver. */
  resolver: Resolver<TValues>;
  /** Applied every time the overlay opens. */
  defaultValues: DefaultValues<TValues>;
  subtitle?: ReactNode;
  icon?: ReactNode;
  /** Text above the fields. */
  description?: ReactNode;
  size?: OverlaySize;
  onSubmit: (values: TValues) => void | Promise<void>;
  submitLabel?: string;
  validateBeforeSubmit?: boolean;
  requireChanges?: boolean;
  cancelLabel?: string;
  /** Shows a Delete action on the left of the footer (edit mode). */
  onDelete?: () => void | Promise<void>;
  deleteLabel?: string;
  /** Ask "Discard changes?" when closing a form with unsaved edits. */
  confirmDiscard?: boolean;
  children: ReactNode;
}
